import { Injectable } from "@angular/core";
import { Prompt } from "@models/prompt.model";
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { SmartFeatureSdk } from "@sdks/smart-feature.sdk";
import { EMPTY, Observable, Subject, forkJoin, of, switchMap, takeUntil, tap, timer } from "rxjs";
import { Store } from "@ngrx/store";
import { PromptSdk } from "@sdks/prompt.sdk";
import { promptsActions } from "../ai-insights/prompts/prompts.actions";
import { uniq } from "lodash-es";
import { AIResponseResource } from "@models/resource/ai-response.resource";
import { Gpt4oEncoding, GptEncoding, MaxTokenList } from "../../constants/gpt-encoding";
import { Clipboard } from '@angular/cdk/clipboard';
import { NotificationManager } from '@managers/notification.manager';
import { ThirdPartyId } from "@models/third-party-id.enum";
import { ClaudeModel } from "@models/amazon-bedrock-claude.model";
import { AppConfigService } from "@services/app-config.service";
import { OpenAIModel } from "@models/open-ai-gpt.model";

const initialState: AiAssistantState = {
    externalAppId: null,
    aiMetadataId: null,
    workflowKey: null,
    aiModel: null,
    prompts: [],
    currentDocIds: '',
    currentPrompt: null,
    currentResponse: [],
    currentResponseIndex: 0,
    hasMoreResponse: false,
    autoContinue: false,
    status: 'initializing'
};

@Injectable({
    providedIn: 'root'
})
export class AiAssistantStore extends ComponentStore<AiAssistantState> {

    constructor(
        private store: Store,
        private smartFeatureSdk: SmartFeatureSdk,
        private promptSdk: PromptSdk,
        private notificationManager: NotificationManager,
        private clipboard: Clipboard,
        private notificationService: NotificationManager,
        private appConfigService: AppConfigService
    ) {
        super({
            externalAppId: null,
            aiMetadataId: null,
            workflowKey: null,
            aiModel: null,
            prompts: [],
            currentDocIds: '',
            currentPrompt: null,
            currentResponse: [],
            currentResponseIndex: 0,
            hasMoreResponse: false,
            autoContinue: false,
            status: 'initializing'
        });
    }

    public prompts$: Observable<Prompt[]> = this.select(state => state.prompts);

    public currentPrompt$: Observable<Prompt> = this.select(state => state.currentPrompt);

    public currentResponse$: Observable<string[]> = this.select(state => state.currentResponse);

    public hasMoreResponse$: Observable<boolean> = this.select(state => state.hasMoreResponse);

    public currentStatus$: Observable<AiAssistantStatus> = this.select(state => state.status);

    public currentDocId$: Observable<string> = this.select(state => typeof state.currentDocIds === 'string' ? state.currentDocIds : '');

    public currentDocIds$: Observable<string[]> = this.select(state => typeof state.currentDocIds !== 'string' ? state.currentDocIds : []);

    public currentWorkflowKey$: Observable<string> = this.select(state => state.workflowKey);

    public readonly addPrompts = this.updater((state, prompts: Prompt[]) => ({ ...state, prompts: prompts }));

    public readonly clearPrompts = this.updater((state) => ({ ...state, prompts: [] }));

    public readonly setPrompt = this.updater((state, prompt: Prompt) => {
        if (state.currentPrompt === prompt)
            return state;

        state.currentPrompt = prompt;
        state.hasMoreResponse = false;
        state.aiMetadataId = null;
        if (prompt) {
            state.currentResponse = [];
            state.currentResponseIndex = 0;
        }
        return state;
    });

    public readonly setDocId = this.updater((state, docId: string) => ({ ...state, currentDocIds: docId, status: 'waiting', currentResponse: [], currentResponseIndex: 0, hasMoreResponse: false, aiMetadataId: null, workflowKey: null }));

    public readonly setDocIds = this.updater((state, docIds: string[]) => ({ ...state, currentDocIds: docIds, status: 'waiting', currentResponse: [], currentResponseIndex: 0, hasMoreResponse: false, aiMetadataId: null, workflowKey: null }));

    public readonly setExternalAppId = this.updater((state, engineModel: number) => {
        if (this.get().status === 'initializing')
            return state;

        state.externalAppId = AiEngineModels.find(x => x.value === engineModel)?.id;
        state.aiModel = AiEngineModels.find(x => x.value === engineModel)?.model;
        state.currentPrompt = null;
        state.currentResponse = [];
        state.currentResponseIndex = 0;
        state.hasMoreResponse = false;
        state.aiMetadataId = null;
        state.workflowKey = null;
        return state;
    });

    public readonly setAutoContinue = this.updater((state, autoContinue: boolean) => ({ ...state, autoContinue: autoContinue }));

    public readonly updateResponse = this.updater((state, response: string) => ({ ...state, currentResponse: [...state.currentResponse, this.parseDocId(response)] }));

    public readonly refreshCurrentPrompt = this.updater((state) => ({ ...state, currentPrompt: state.prompts.find(x => x.promptId === state.currentPrompt.promptId) }));

    public readonly cancelRequest = this.updater((state) => {
        this.cancel$.next();
        this.cancel$.complete();
        state.aiMetadataId = null;
        state.workflowKey = null;
        state.status = 'waiting';
        state.hasMoreResponse = false;
        return state;
    });

    public readonly initialize = this.updater((state, engineModel: number) => {
        state = initialState;
        state.externalAppId = AiEngineModels.find(x => x.value === engineModel)?.id;
        state.aiModel = AiEngineModels.find(x => x.value === engineModel)?.model;
        return state;
    });

    public readonly answerPrompt = this.effect((trigger$) => {
        return trigger$.pipe(
            tap(() => {
                this.patchState({ status: 'responding', currentResponse: [], hasMoreResponse: false, aiMetadataId: null, workflowKey: null, currentResponseIndex: 0 });
                this.cancel$ = new Subject<void>();
            }),
            switchMap(() => {
                return this.initialApiRequest();
            })
        );
    });

    public readonly createAndPrompt = this.effect<string>((prompt$) => {
        return prompt$.pipe(
            tap(() => {
                this.patchState({ status: 'prompting', currentResponse: [], hasMoreResponse: false, aiMetadataId: null, workflowKey: null, currentResponseIndex: 0 });
            }),
            switchMap((prompt) => {
                return forkJoin([
                    this.promptSdk.post(`Recent Prompt ${new Date().toLocaleString()}`, prompt),
                    this.smartFeatureSdk.generatePromptTitle(prompt, this.get().externalAppId)
                ]).pipe(
                    tapResponse(
                        (data) => {
                            if (data[1] && data[1].length > 0 && data[1].length < 50) {
                                this.updatePromptTitle({ promptId: data[0].promptId, title: data[1] });
                            }
                            this.patchState({ currentPrompt: data[0] });
                            this.store.dispatch(promptsActions.loadPrompts({ initLoad: false }));
                        },
                        () => this.displayErrorNotification('creating the prompt'),
                        () => this.answerPrompt()
                    )
                );
            })
        )
    });

    public readonly deletePrompt = this.effect<string>((promptId$) => {
        return promptId$.pipe(
            tap((id) => {
                const current = this.get().currentPrompt;
                if (current && current.promptId === id)
                    this.setPrompt(null);
            }),
            switchMap((id) => {
                return this.promptSdk.delete(id).pipe(
                    tapResponse(
                        (isDeleted) => { if (isDeleted) this.store.dispatch(promptsActions.loadPrompts({ initLoad: false })); },
                        () => this.displayErrorNotification('deleting the prompt')
                    )
                )
            })
        )
    });

    public readonly retryPrompt = this.effect((trigger$) => {
        return trigger$.pipe(
            tap(() => this.patchState({ status: 'prompting', currentResponse: this.get().currentResponse.slice(0, -1) })),
            switchMap(() => {
                this.patchState({ currentResponseIndex: this.get().currentResponse.length - 1 < 0 ? 0 : this.get().currentResponse.length - 1 })
                this.continuePrompt(true);
                return EMPTY;
            })
        )
    });

    public readonly getNextChunk = this.effect((trigger$) => {
        return trigger$.pipe(
            tap(() => {
                this.patchState({ currentResponseIndex: this.get().currentResponseIndex + 1, workflowKey: null })
            }),
            switchMap(() => {
                this.continuePrompt(false);
                return EMPTY;
            })
        )
    });

    public displayTokenLimitWarning(model: string, text: string): boolean {
        return this.exceedPercentOfTotalToken(model, text, 0.25);
    }

    public displayTokenLimitError(model: string, text: string): boolean {
        return this.exceedPercentOfTotalToken(model, text, 0.50);
    }

    public copyResponseToClipboard(): void {
        const responses = this.get().currentResponse;
        this.clipboard.copy(responses.join('\n\n')
            .replaceAll('" target="DocViewer">link</a>]', '] ')
            .replaceAll('<a href="/review/doc-viewer?', `${this.appConfigService.getConfig().webApi}/review/doc-viewer?`));
        this.notificationService.displaySnackBarMessage({
            message: 'CoPilot response copied to clipboard!'
        });
    }

    public readonly scheduleStreaming = this.effect<{ waitInMs: number; autoScroll: () => void }>(
        (trigger$) => trigger$.pipe(
            switchMap(({ waitInMs, autoScroll }) =>
                timer(waitInMs).pipe(
                    switchMap(() => {
                        const workflowKey = this.get().workflowKey;
                        if (!workflowKey) return of(null);
                        return this.smartFeatureSdk.getResponse(workflowKey);
                    }),
                    tapResponse({
                        next: (response) => {
                            if (!response)
                                this.scheduleStreaming({ waitInMs: 3000, autoScroll });
                            else {
                                this.updateResponseByIndex(response.result);
                                if (response.result.length > 0)
                                    autoScroll();
                                if (response.isError)
                                    this.patchState({ status: 'error' });
                                if (response.completed)
                                    this.completeResponse(response);
                                if (!response.isError && !response.completed)
                                    this.scheduleStreaming({ waitInMs: 1000, autoScroll });
                            }
                        },
                        error: () => {
                            this.patchState({ status: 'error', hasMoreResponse: false });
                            this.updateResponse(this.errorResponse);
                        }
                    })
                )
            )
        )
    );

    private readonly handleResponse = this.effect<string>((workflowKey$) => {
        return workflowKey$.pipe(
            switchMap((workflowKey: string) => {
                this.patchState({ workflowKey: workflowKey });
                return EMPTY;
            })
        );
    });

    private readonly updateResponseByIndex = this.updater((state, response: string) => {
        state.currentResponse[state.currentResponseIndex] = this.parseDocId(response);
        return state;
    });

    private readonly completeResponse = this.effect<AIResponseResource>((response$) => {
        return response$.pipe(
            tap((response) => {
                this.patchState({ workflowKey: null, aiMetadataId: response.aiMetadataId, hasMoreResponse: response.hasMoreResponse })
            }),
            switchMap((response) => {
                if (response.hasMoreResponse && this.get().autoContinue) {
                    this.getNextChunk();
                    return EMPTY;
                }
                this.patchState({ status: 'waiting' });
                return EMPTY;
            })
        )
    });

    private readonly continuePrompt = this.effect<boolean>((isRetry$) => {
        return isRetry$.pipe(
            tap((isRetry) => {
                if (isRetry && this.get().currentResponseIndex == 0)
                    this.patchState({ status: 'responding' });
                else
                    this.patchState({ status: 'continuing' });
            }),
            switchMap((isRetry: boolean) => {
                return this.continueApiRequest(isRetry);
            })
        )
    });

    private initialApiRequest(): Observable<string> {
        let docIds: string[] = [];
        docIds = docIds.concat(this.get().currentDocIds);
        const aiRequest: Observable<string> = this.smartFeatureSdk.generateResponse(
            {
                externalAppId: this.get().externalAppId,
                promptId: this.get().currentPrompt.promptId,
                docIds: docIds,
                aiModel: this.get().aiModel
            }).pipe(
                tapResponse((workflowKey) => this.handleResponse(workflowKey),
                    () => {
                        this.patchState({ status: 'error', hasMoreResponse: false });
                        this.updateResponse(this.errorResponse);
                    }));
        return aiRequest.pipe(takeUntil(this.cancel$));
    }

    private continueApiRequest(isRetry: boolean): Observable<string> {
        const aiRequest: Observable<string> = this.smartFeatureSdk.generateResponseContinue(
            {
                aiMetadataId: this.get().aiMetadataId,
                isRetry: isRetry
            }).pipe(
                tapResponse((workflowKey) => this.handleResponse(workflowKey),
                    () => {
                        this.patchState({ status: 'error', hasMoreResponse: false });
                        this.updateResponse(this.errorResponse);
                    }));
        return aiRequest.pipe(takeUntil(this.cancel$));
    }

    private readonly updatePromptTitle = this.effect<PromptPatchParameter>((parameter$) => {
        return parameter$.pipe(
            switchMap((parameter) => this.promptSdk.patch(parameter.promptId, { title: parameter.title }).pipe(
                tapResponse(
                    (data) => this.patchState({ currentPrompt: data }),
                    () => this.displayErrorNotification('updating the prompt'),
                    () => this.store.dispatch(promptsActions.loadPrompts({ initLoad: false }))
                ))
            )
        )
    });

    private displayErrorNotification(message: string): void {
        this.notificationManager.displaySnackBarMessage({
            message: `An error has occurred when ${message}. Please try again or contact Professional Service for support.`,
            type: 'error',
            ttl: 5000
        });
    }

    private parseDocId(text: string): string {
        const guidRegex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/gi;
        const matches = text.match(guidRegex);
        let result = text;
        if (matches) {
            const docIds = uniq(matches);
            docIds.forEach((docId) => {
                if (result.indexOf(docId) > -1) {
                    result = result.replaceAll(docId, `${docId} [<a href="/review/doc-viewer?DocId=${docId}" target="DocViewer">link</a>]`);
                }
            });
        }
        return result;
    }

    private exceedPercentOfTotalToken(model: string, text: string, percent: number) {
        if (this.get().externalAppId == ThirdPartyId.OpenAi) {
            let tokenCount = 0;
            if (model === OpenAIModel.GPT4Omni || model === OpenAIModel.GPT4OmniMini)
                tokenCount = Gpt4oEncoding.encode(text).length;
            else
                tokenCount = GptEncoding.encode(text).length;
            const maxContextTokenCount = MaxTokenList.find(x => x.model === model).maxToken;
            if (tokenCount > maxContextTokenCount * percent)
                return true;
        }
        return false;
    }

    private errorResponse: string = 'An error has occurred. Please try again after a few minutes, or contact Professional Services for more support.';
    private cancel$: Subject<void>;
}

export type AiAssistantStatus = 'initializing' | 'waiting' | 'responding' | 'continuing' | 'prompting' | 'error'

export const AiEngineModels = [
    { value: 0, id: ThirdPartyId.OpenAi, model: null, name: 'OpenAI' },
    { value: 1, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_Sonnet, name: 'Claude 3 Sonnet' },
    { value: 2, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_5_Sonnet, name: 'Claude 3.5 Sonnet' },
    { value: 3, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_5_SonnetV2, name: 'Claude 3.5 Sonnet v2' },
    { value: 4, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_7_Sonnet, name: 'Claude 3.7 Sonnet' },
    { value: 5, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude4Sonnet, name: 'Claude 4 Sonnet' },
    { value: 6, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude4Opus, name: 'Claude 4 Opus' }
];

export interface AiAssistantState {
    externalAppId: string;
    aiMetadataId?: string;
    workflowKey?: string;
    aiModel: string;
    prompts: Prompt[],
    currentPrompt: Prompt;
    currentResponse: string[];
    currentResponseIndex: number;
    hasMoreResponse: boolean;
    currentDocIds: string | string[];
    autoContinue: boolean;
    status: AiAssistantStatus;
}

export interface PromptPatchParameter {
    promptId: string;
    title: string;
}
